import React, { useState, useEffect } from "react";
import api from "../api";
import { useQuote } from "../context/QuoteContext";
import { formatEur, formatDateTimeIt } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Search, Store, Warehouse, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const ICONS = { store: Store, warehouse: Warehouse };

export default function MarketVerifySection({
  area,
  endpoint,
  storeKey,
  title,
  description,
  testidPrefix,
  icon = "store",
  defaultCitta = "Milano",
  editableArea = false,
}) {
  const { areaMarkets, setAreaMarket, ritiro } = useQuote();
  const stored = (areaMarkets && areaMarkets[storeKey]) || {};
  const cap = (ritiro && ritiro.cap) || "";
  const [citta, setCitta] = useState(stored.citta || (ritiro && ritiro.citta) || defaultCitta);
  const [cittaEdited, setCittaEdited] = useState(false);
  const [areaVal, setAreaVal] = useState(stored.area || area || 0);
  const [areaEdited, setAreaEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const Icon = ICONS[icon] || Store;

  // follow the derived area until the user manually edits the field
  useEffect(() => {
    if (editableArea && !areaEdited && !stored.area) setAreaVal(area || 0);
  }, [area, editableArea, areaEdited, stored.area]);

  // follow the pickup city (from CAP lookup) until the user manually edits it
  useEffect(() => {
    if (!cittaEdited && !stored.citta && ritiro && ritiro.citta) setCitta(ritiro.citta);
  }, [ritiro, cittaEdited, stored.citta]);

  const effArea = editableArea ? areaVal : area;

  const examples = stored.examples || [];
  const analyzedAt = stored.analyzedAt || null;

  const verify = async () => {
    if (!effArea || effArea <= 0) {
      toast.error("Inserisci prima la metratura (m²)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { area: effArea, citta: citta || defaultCitta, cap });
      setAreaMarket(storeKey, {
        citta: citta || defaultCitta,
        cap,
        area: effArea,
        examples: data.examples || [],
        analyzedAt: new Date().toISOString(),
      });
      toast.success(cap ? `Mercato verificato nella zona ${cap}` : "Mercato verificato");
    } catch {
      toast.error("Impossibile verificare il mercato");
    } finally {
      setLoading(false);
    }
  };

  const avgMonthly = examples.length
    ? examples.reduce((s, e) => s + (Number(e.monthlyPrice) || 0), 0) / examples.length
    : 0;
  const avgPerM2 = examples.length
    ? examples.reduce((s, e) => s + (Number(e.sizeM2) > 0 ? Number(e.monthlyPrice) / Number(e.sizeM2) : 0), 0) / examples.length
    : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid={`${testidPrefix}-market`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5 text-[#1a5f5a]" />
        <h3 className="font-display font-semibold text-lg">{title}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">{description}</p>

      <div className="flex items-end gap-3 flex-wrap">
        {editableArea && (
          <div>
            <Label className="text-xs flex items-center gap-1">Metratura (m²)</Label>
            <Input type="number" value={areaVal || ""} onChange={(e) => { setAreaEdited(true); setAreaVal(parseFloat(e.target.value) || 0); }}
              placeholder="0" className="mt-1 w-28" data-testid={`${testidPrefix}-area-input`} />
          </div>
        )}
        <div>
          <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Città {cap ? <span className="text-[10px] text-slate-400">· zona CAP {cap}</span> : null}</Label>
          <Input value={citta} onChange={(e) => { setCittaEdited(true); setCitta(e.target.value); }} placeholder={defaultCitta}
            className="mt-1 w-48" data-testid={`${testidPrefix}-citta-input`} />
        </div>
        <Button onClick={verify} disabled={loading || !effArea} className="bg-[#1a5f5a] hover:bg-[#134744] gap-1.5 h-10" data-testid={`${testidPrefix}-verify-button`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Verifica il mercato
        </Button>
        {effArea > 0 && <span className="text-[11px] text-slate-400 pb-2">Target ~{Number(effArea).toFixed(1)} m² (±10%)</span>}
      </div>

      {examples.length > 0 && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wide text-slate-400">3 esempi più vicini</div>
            {analyzedAt && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-400" data-testid={`${testidPrefix}-analyzed-at`}>
                <Clock className="h-3 w-3" /> Analisi del {formatDateTimeIt(analyzedAt)}
              </div>
            )}
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <th className="text-left p-2.5 font-medium">Annuncio</th>
                  <th className="text-right p-2.5 font-medium">m²</th>
                  <th className="text-right p-2.5 font-medium">€/mese</th>
                  <th className="text-right p-2.5 font-medium">€/m²</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {examples.map((e, i) => (
                  <tr key={i} data-testid={`${testidPrefix}-example-${i}`}>
                    <td className="p-2.5 text-slate-700">{e.name}<div className="text-[11px] text-slate-400">{e.zona}{e.note ? ` · ${e.note}` : ""}</div></td>
                    <td className="p-2.5 text-right text-slate-600">{Number(e.sizeM2).toFixed(1)}</td>
                    <td className="p-2.5 text-right font-medium">€{formatEur(e.monthlyPrice)}</td>
                    <td className="p-2.5 text-right text-slate-500">€{formatEur(Number(e.sizeM2) > 0 ? e.monthlyPrice / e.sizeM2 : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-[#1a5f5a]/10 border border-[#1a5f5a]/15 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#134744]">Costo medio</div>
              <div className="text-xl font-display font-semibold text-[#1a5f5a] mt-1" data-testid={`${testidPrefix}-avg`}>€{formatEur(avgMonthly)}<span className="text-xs font-normal text-slate-400">/mese</span></div>
            </div>
            <div className="bg-[#1a5f5a]/10 border border-[#1a5f5a]/15 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#134744]">Costo medio per m²</div>
              <div className="text-xl font-display font-semibold text-[#1a5f5a] mt-1" data-testid={`${testidPrefix}-avg-m2`}>€{formatEur(avgPerM2)}<span className="text-xs font-normal text-slate-400">/m²</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
